// src/pages/FinancialInformation.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import Select from "react-select";

import axios from "../axiosConfig";

import {
  useCompanySettingsData,
} from "../hooks/useCompanySettingsData";

import "../styles/FinancialInformation.css";

const CURRENCIES = [
  "EUR",
  "USD",
  "GBP",
  "CHF",
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "HUF",
  "RON",
  "BGN",
];

const CURRENCY_OPTIONS =
  CURRENCIES.map(
    (
      currency
    ) => ({
      value:
        currency,

      label:
        currency,
    })
  );

const EMPTY_FINANCIAL_FORM = {
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  iban: "",
  swift: "",
  currency: "EUR",
  invoiceSendingEmail: "",
};

const formatIban = (
  value
) => {
  const raw =
    (value || "")
      .replace(
        /[^a-z0-9]/gi,
        ""
      )
      .toUpperCase();

  return (
    raw.match(
      /.{1,4}/g
    )?.join(" ") ??
    ""
  );
};

const financialFromData = (
  data
) => {
  if (!data) {
    return {
      ...EMPTY_FINANCIAL_FORM,
    };
  }

  return {
    bankName:
      data.bankName ||
      "",

    accountHolder:
      data.accountHolder ||
      "",

    accountNumber:
      data.accountNumber ||
      "",

    iban:
      formatIban(
        data.iban ||
          ""
      ),

    swift:
      (
        data.bic ||
        data.swift ||
        ""
      ).toUpperCase(),

    currency:
      data.currency ||
      "EUR",

    invoiceSendingEmail:
      data.invoiceSendingEmail ||
      "",
  };
};

export default function FinancialInformation({
  embedded = false,
  onSaved,
}) {
  const { t } =
    useTranslation(
      "financial_information"
    );

  const {
    data,
    refresh,
    patchSection,
  } =
    useCompanySettingsData();

  const financial =
    data?.financial ||
    null;

  const dirtyRef =
    useRef(false);

  const [
    form,
    setForm,
  ] = useState(
    () =>
      financialFromData(
        financial
      )
  );

  const [
    saving,
    setSaving,
  ] = useState(false);

  const currencySelectStyles =
    useMemo(
      () => ({
        control: (
          base,
          state
        ) => ({
          ...base,

          minHeight:
            42,

          height:
            42,

          borderRadius:
            12,

          borderColor:
            state.isFocused
              ? "rgba(59, 130, 246, 0.75)"
              : "rgb(116, 122, 134)",

          backgroundColor:
            "transparent",

          boxShadow:
            state.isFocused
              ? "0 0 0 3px rgba(59, 130, 246, 0.14)"
              : "none",

          cursor:
            "pointer",

          transition:
            "border-color 160ms ease, box-shadow 160ms ease",

          "&:hover": {
            borderColor:
              state.isFocused
                ? "rgba(59, 130, 246, 0.75)"
                : "rgba(148, 163, 184, 0.95)",
          },
        }),

        valueContainer: (
          base
        ) => ({
          ...base,

          height:
            42,

          padding:
            "0 12px",
        }),

        singleValue: (
          base
        ) => ({
          ...base,

          color:
            "#fff",

          fontSize:
            14,

          fontWeight:
            600,
        }),

        placeholder: (
          base
        ) => ({
          ...base,

          color:
            "rgba(148, 163, 184, 0.78)",
        }),

        input: (
          base
        ) => ({
          ...base,

          color:
            "#fff",

          margin:
            0,

          padding:
            0,
        }),

        indicatorsContainer: (
          base
        ) => ({
          ...base,

          height:
            42,
        }),

        dropdownIndicator: (
          base,
          state
        ) => ({
          ...base,

          color:
            state.isFocused
              ? "rgba(96, 165, 250, 1)"
              : "rgba(148, 163, 184, 0.95)",

          paddingRight:
            12,

          transition:
            "transform 160ms ease, color 160ms ease",

          transform:
            state.selectProps.menuIsOpen
              ? "rotate(180deg)"
              : "rotate(0deg)",

          "&:hover": {
            color:
              "rgba(96, 165, 250, 1)",
          },
        }),

        indicatorSeparator:
          () => ({
            display:
              "none",
          }),

        menu: (
          base
        ) => ({
          ...base,

          marginTop:
            8,

          borderRadius:
            14,

          overflow:
            "hidden",

          background:
            "linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 0.98))",

          border:
            "1px solid rgba(148, 163, 184, 0.22)",

          boxShadow:
            "0 18px 44px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(255, 255, 255, 0.04) inset",

          zIndex:
            50,
        }),

        menuList: (
          base
        ) => ({
          ...base,

          padding:
            6,

          maxHeight:
            230,
        }),

        option: (
          base,
          state
        ) => ({
          ...base,

          minHeight:
            38,

          borderRadius:
            10,

          padding:
            "9px 11px",

          fontSize:
            13,

          fontWeight:
            state.isSelected
              ? 800
              : 600,

          cursor:
            "pointer",

          color:
            state.isSelected
              ? "#fff"
              : state.isFocused
                ? "rgba(226, 232, 240, 1)"
                : "rgba(203, 213, 225, 0.92)",

          backgroundColor:
            state.isSelected
              ? "rgba(59, 130, 246, 0.88)"
              : state.isFocused
                ? "rgba(59, 130, 246, 0.16)"
                : "transparent",

          "&:active": {
            backgroundColor:
              "rgba(59, 130, 246, 0.28)",
          },
        }),
      }),
      []
    );

  useEffect(() => {
    if (
      !financial ||
      dirtyRef.current
    ) {
      return;
    }

    setForm(
      financialFromData(
        financial
      )
    );
  }, [
    financial,
  ]);

  const setField = (
    key,
    value
  ) => {
    dirtyRef.current =
      true;

    setForm(
      (
        previous
      ) => ({
        ...previous,

        [key]:
          value,
      })
    );
  };

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      const payload = {
        ...form,

        iban:
          form.iban
            .replace(
              /\s+/g,
              ""
            )
            .toUpperCase(),

        swift:
          form.swift
            .replace(
              /\s+/g,
              ""
            )
            .toUpperCase(),

        invoiceSendingEmail:
          form.invoiceSendingEmail
            .trim()
            .toLowerCase(),
      };

      setSaving(
        true
      );

      try {
        const response =
          await axios.post(
            "/company/financial-information",
            payload
          );

        const saved =
          response?.data &&
          typeof response.data ===
            "object"
            ? {
                ...payload,
                ...response.data,
              }
            : payload;

        dirtyRef.current =
          false;

        setForm(
          financialFromData(
            saved
          )
        );

        patchSection(
          "financial",
          saved
        );

        if (
          typeof onSaved ===
          "function"
        ) {
          await onSaved();
        } else {
          await refresh();
        }

        console.log(
          "Saved Financial Information:",
          saved
        );

        alert(
          t(
            "toasts.saved_demo"
          )
        );
      } catch (error) {
        console.error(
          "[FinancialInformation] Failed to save /company/financial-information:",
          error
        );

        alert(
          "Não foi possível guardar os dados financeiros da empresa."
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  return (
    <div
      className={`financial-information ${
        embedded
          ? "financial-information--embedded"
          : ""
      }`}
    >
      <div className="financial-information__header">
        <h1 className="financial-information__title">
          {t(
            "title"
          )}
        </h1>

        <p className="financial-information__subtitle">
          {t(
            "subtitle"
          )}
        </p>
      </div>

      <form
        className="financial-information__form"
        onSubmit={
          handleSubmit
        }
      >
        <div className="financial-information__grid">
          <div className="financial-information__field">
            <label className="financial-information__label">
              {t(
                "fields.bankName.label"
              )}
            </label>

            <input
              className="financial-information__input"
              required
              value={
                form.bankName
              }
              onChange={(
                event
              ) =>
                setField(
                  "bankName",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.bankName.placeholder"
              )}
            />
          </div>

          <div className="financial-information__field">
            <label className="financial-information__label">
              {t(
                "fields.accountHolder.label"
              )}
            </label>

            <input
              className="financial-information__input"
              required
              value={
                form.accountHolder
              }
              onChange={(
                event
              ) =>
                setField(
                  "accountHolder",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.accountHolder.placeholder"
              )}
            />
          </div>

          <div className="financial-information__field">
            <label className="financial-information__label">
              {t(
                "fields.accountNumber.label"
              )}
            </label>

            <input
              className="financial-information__input"
              value={
                form.accountNumber
              }
              onChange={(
                event
              ) =>
                setField(
                  "accountNumber",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.accountNumber.placeholder"
              )}
            />
          </div>

          <div className="financial-information__field">
            <label className="financial-information__label">
              {t(
                "fields.iban.label"
              )}
            </label>

            <input
              className="financial-information__input"
              required
              value={
                form.iban
              }
              onChange={(
                event
              ) =>
                setField(
                  "iban",
                  formatIban(
                    event.target.value
                  )
                )
              }
              placeholder={t(
                "fields.iban.placeholder"
              )}
              inputMode="text"
              autoCapitalize="characters"
            />
          </div>

          <div className="financial-information__field">
            <label className="financial-information__label">
              {t(
                "fields.swift.label"
              )}
            </label>

            <input
              className="financial-information__input"
              required
              value={
                form.swift
              }
              onChange={(
                event
              ) =>
                setField(
                  "swift",
                  event.target.value.toUpperCase()
                )
              }
              placeholder={t(
                "fields.swift.placeholder"
              )}
              autoCapitalize="characters"
            />
          </div>

          <div className="financial-information__field">
            <label className="financial-information__label">
              {t(
                "fields.currency.label"
              )}
            </label>

            <Select
              className="financial-information__select-wrap"
              classNamePrefix="financial-information-currency"
              styles={
                currencySelectStyles
              }
              value={
                CURRENCY_OPTIONS.find(
                  (
                    option
                  ) =>
                    option.value ===
                    form.currency
                ) ||
                CURRENCY_OPTIONS[0]
              }
              onChange={(
                option
              ) =>
                setField(
                  "currency",
                  option?.value ||
                    "EUR"
                )
              }
              options={
                CURRENCY_OPTIONS
              }
              isSearchable={
                false
              }
              menuPlacement="auto"
            />
          </div>

          <div className="financial-information__field">
            <label className="financial-information__label">
              {t(
                "fields.invoiceSendingEmail.label"
              )}
            </label>

            <input
              className="financial-information__input"
              type="email"
              required
              value={
                form.invoiceSendingEmail
              }
              onChange={(
                event
              ) =>
                setField(
                  "invoiceSendingEmail",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.invoiceSendingEmail.placeholder"
              )}
              autoComplete="email"
            />
          </div>
        </div>

        <div className="financial-information__actions">
          <button
            type="button"
            className="financial-information__btn financial-information__btn--ghost"
            onClick={() =>
              window.history.back()
            }
            disabled={
              saving
            }
          >
            {t(
              "actions.cancel"
            )}
          </button>

          <button
            type="submit"
            className="financial-information__btn financial-information__btn--primary"
            disabled={
              saving
            }
          >
            {saving
              ? t(
                  "actions.saving",
                  "Saving..."
                )
              : t(
                  "actions.save"
                )}
          </button>
        </div>
      </form>
    </div>
  );
}